"use client";

import { memo } from "react";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertTriangle } from "lucide-react";

interface AccessibilityLevel {
  level: "AAA" | "AA" | "fail";
  ratio: number;
}

interface AccessibilityInfoProps {
  accessibilityInfo: AccessibilityLevel | false;
}

export const AccessibilityInfo = memo(function AccessibilityInfo({
  accessibilityInfo,
}: AccessibilityInfoProps) {
  if (!accessibilityInfo) return null;

  return (
    <div className="p-4 rounded-lg border bg-card">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium">Accessibility</Label>
        <div
          className={`flex items-center gap-1 ${
            accessibilityInfo.level === "fail"
              ? "text-yellow-600"
              : "text-green-600"
          }`}
        >
          {accessibilityInfo.level === "fail" ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          <span className="text-xs font-medium">
            {accessibilityInfo.level === "AAA" && "WCAG AAA"}
            {accessibilityInfo.level === "AA" && "WCAG AA"}
            {accessibilityInfo.level === "fail" && "Low Contrast"}
          </span>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        <div className="flex justify-between">
          <span>Contrast Ratio:</span>
          <span className="font-mono">
            {accessibilityInfo.ratio.toFixed(2)}:1
          </span>
        </div>
        <p
          className={`mt-2 ${
            accessibilityInfo.level === "fail"
              ? "text-yellow-600"
              : "text-green-600"
          }`}
        >
          {accessibilityInfo.level === "fail" &&
            "This color combination may be difficult to read for users with visual impairments. Consider choosing colors with higher contrast."}
          {accessibilityInfo.level === "AA" &&
            "This color combination meets WCAG AA standards for normal text."}
          {accessibilityInfo.level === "AAA" &&
            "This color combination meets the highest WCAG AAA standards."}
        </p>
      </div>
    </div>
  );
});
